import { getLogger } from '@blueberry/observability'
import { poll } from '@blueberry/utils/common'
import { Flow, SignupData, SignupResponseSuccess } from '../types'

const logger = getLogger('signup/analytics/partnero')

type PartneroCustomerSignup = (
  resource: 'customers',
  action: 'signup',
  data: {
    data: {
      key: string // customer email
      name: string
      email: string
    }
  },
) => void

type PartneroTransactionCreate = (
  resource: 'transactions',
  action: 'create',
  data: {
    data: {
      key: string
      amount: number
      amount_units: 'usd'
      customer: {
        key: string // customer email
      }
    }
  },
) => void

declare global {
  interface Window {
    po: PartneroCustomerSignup | PartneroTransactionCreate
  }
}

export const partnero = {
  async trackStep(flow: Flow, step: number, data: SignupData) {
    if (flow !== 'd2c') return

    const po = await poll(() => window.po as PartneroCustomerSignup)
    if (!po) {
      logger.error('Retries exhausted waiting for Partnero to initialize')
      return
    }

    if (step === 2) {
      try {
        po('customers', 'signup', {
          data: {
            key: data.account?.email || '',
            email: data.account?.email || '',
            name: `${data.account?.first_name || ''} ${data.account?.last_name || ''}`,
          },
        })
      } catch (error) {
        logger.error('Error tracking Partnero customer signup event:', error)
        return
      }
    }
  },
  async trackSuccess(
    flow: Flow,
    submitResponse: SignupResponseSuccess,
    eventID: string,
  ) {
    if (flow !== 'd2c') return
    if (submitResponse.flow !== 'd2c') {
      throw new Error(
        `Incorrect response flow. Got ${submitResponse.flow}, expected d2c.`,
      )
    }

    const po = await poll(() => window.po as PartneroTransactionCreate)
    if (!po) {
      logger.error('Retries exhausted waiting for Partnero to initialize')
      return
    }

    try {
      po('transactions', 'create', {
        data: {
          key: eventID,
          amount: submitResponse.totalPrice,
          amount_units: 'usd',
          customer: {
            key: submitResponse.hashedProfileId,
          },
        },
      })
    } catch (error) {
      logger.error('Error tracking Partnero transaction create event:', error)
      return
    }
  },
}
